import { AUTO_PRINT_SCRIPT, escapeHtml } from '../../../lib/printDocument';

/**
 * The transfer packing list: a standalone, printable HTML document (docs/warehouse.md). Every
 * value is escaped; dates and labels arrive already formatted, so this module stays pure. The
 * page is a white sheet in the Green palette (as `.theme-paper` documents are), whatever theme
 * the app is in, because it opens in its own window.
 */

export type PackingListLine = {
  name: string;
  sku: string | null;
  category: string | null;
  qty: number;
  /** Serial, barcode or asset codes on the line (serialized lines). */
  serials: readonly string[];
  tracking: 'serial' | 'quantity';
  notes?: string | null;
};

export type PackingListInput = {
  docNo: string;
  /** The transfer's description. */
  title: string | null;
  fromSite: string;
  toSite: string;
  /** e.g. "Temporary · back Oct 14". */
  scopeLabel?: string | null;
  needBy?: string | null;
  ships?: string | null;
  arrives?: string | null;
  /** e.g. "Company truck · Box truck 14". */
  logistics?: string | null;
  /** e.g. "0917102 - Riverside Schools Fall Gala". */
  events?: readonly string[];
  lines: readonly PackingListLine[];
  /** e.g. "Oct 7, 9:41 AM". */
  printedAt: string;
  /** Opens the print dialog once the document has loaded. */
  autoPrint?: boolean;
};

const STYLES = `
*{box-sizing:border-box}
html{color-scheme:light}
body{margin:0;background:#ffffff;color:#1e293b;font-family:system-ui,-apple-system,'Segoe UI',Roboto,'Helvetica Neue',sans-serif;font-size:12px;line-height:1.45}
.sheet{max-width:960px;margin:0 auto;padding:28px 32px 40px}
.head{display:flex;justify-content:space-between;align-items:flex-start;gap:24px;padding-bottom:14px;border-bottom:2px solid #1e293b}
.eyebrow{margin:0;color:#0b7a6e;font-size:10px;font-weight:800;letter-spacing:.08em;text-transform:uppercase}
h1{margin:4px 0 0;font-size:22px;line-height:1.15}
.subtitle{margin:4px 0 0;color:#475569;font-size:13px}
.doc{text-align:right}
.doc strong{display:block;font-size:18px;letter-spacing:.02em}
.doc span{color:#64748b;font-size:11px}
.facts{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:10px 24px;margin:16px 0 18px}
.fact span{display:block;color:#64748b;font-size:10px;font-weight:700;letter-spacing:.05em;text-transform:uppercase}
.fact strong{display:block;margin-top:2px;font-size:12px;font-weight:650}
table{width:100%;border-collapse:collapse}
thead th{padding:6px 8px;border-bottom:1px solid #94a3b8;color:#475569;font-size:10px;font-weight:800;letter-spacing:.05em;text-align:left;text-transform:uppercase}
td{padding:7px 8px;border-bottom:1px solid #e2e8f0;vertical-align:top}
tr{break-inside:avoid}
.group th{padding:12px 8px 5px;border-bottom:1px solid #cbd5e1;color:#1e293b;font-size:11px;font-weight:800;letter-spacing:.04em;text-align:left;text-transform:uppercase}
.check{width:28px}
.box{display:inline-block;width:13px;height:13px;border:1.5px solid #475569;border-radius:2px}
.qty{width:56px;text-align:right;font-variant-numeric:tabular-nums;font-weight:700}
.sku{width:140px;color:#334155;font-family:ui-monospace,SFMono-Regular,Menlo,Consolas,monospace;font-size:11px}
.name strong{font-weight:650}
.serials{display:block;margin-top:3px;color:#475569;font-family:ui-monospace,SFMono-Regular,Menlo,Consolas,monospace;font-size:10.5px;overflow-wrap:anywhere}
.note{display:block;margin-top:2px;color:#64748b;font-size:11px}
.totals{display:flex;justify-content:flex-end;gap:28px;margin-top:12px;padding-top:10px;border-top:2px solid #1e293b;font-size:12px}
.totals span{color:#64748b}
.totals strong{margin-left:6px;font-variant-numeric:tabular-nums}
.signatures{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:24px;margin-top:36px}
.signature{padding-top:26px;border-top:1px solid #1e293b;color:#475569;font-size:10px;font-weight:700;letter-spacing:.05em;text-transform:uppercase}
.foot{margin-top:24px;color:#94a3b8;font-size:10px;text-align:right}
.empty{padding:18px 8px;color:#64748b;text-align:center}
@page{margin:14mm}
@media print{.sheet{padding:0}}
`;

function groupLines(lines: readonly PackingListLine[]): Array<{ label: string; lines: PackingListLine[] }> {
  const groups: Array<{ label: string; lines: PackingListLine[] }> = [];
  const byLabel = new Map<string, { label: string; lines: PackingListLine[] }>();
  for (const line of lines) {
    const label = line.category?.trim() || 'Other';
    let group = byLabel.get(label);
    if (!group) {
      group = { label, lines: [] };
      byLabel.set(label, group);
      groups.push(group);
    }
    group.lines.push(line);
  }
  return groups;
}

function fact(label: string, value: string | null | undefined): string {
  if (!value) return '';
  return `<div class="fact"><span>${escapeHtml(label)}</span><strong>${escapeHtml(value)}</strong></div>`;
}

function lineRow(line: PackingListLine): string {
  const serials = line.serials.length
    ? `<span class="serials">${escapeHtml(line.serials.join(', '))}</span>`
    : '';
  const note = line.notes ? `<span class="note">${escapeHtml(line.notes)}</span>` : '';
  const tracking = line.tracking === 'quantity' && !line.serials.length ? '<span class="note">By quantity</span>' : '';
  return [
    '<tr>',
    '<td class="check"><span class="box" aria-hidden="true"></span></td>',
    `<td class="name"><strong>${escapeHtml(line.name)}</strong>${serials}${tracking}${note}</td>`,
    `<td class="sku">${escapeHtml(line.sku ?? '')}</td>`,
    `<td class="qty">${escapeHtml(String(line.qty))}</td>`,
    '</tr>',
  ].join('');
}

export function buildPackingListHtml(input: PackingListInput): string {
  const groups = groupLines(input.lines);
  const units = input.lines.reduce((sum, line) => sum + (Number.isFinite(line.qty) ? line.qty : 0), 0);
  const serialized = input.lines.reduce((sum, line) => sum + line.serials.length, 0);
  const title = input.title?.trim() || 'Transfer';
  const body = groups.length
    ? groups.map((group) => [
      `<tr class="group"><th colspan="4" scope="colgroup">${escapeHtml(group.label)}</th></tr>`,
      ...group.lines.map(lineRow),
    ].join('')).join('')
    : '<tr><td class="empty" colspan="4">This transfer has no lines.</td></tr>';
  const events = input.events && input.events.length ? input.events.join('; ') : null;

  return [
    '<!doctype html>',
    '<html lang="en">',
    '<head>',
    '<meta charset="utf-8">',
    '<meta name="viewport" content="width=device-width, initial-scale=1">',
    `<title>${escapeHtml(`Packing list ${input.docNo}`)}</title>`,
    `<style>${STYLES}</style>`,
    '</head>',
    '<body>',
    '<main class="sheet">',
    '<header class="head">',
    '<div>',
    '<p class="eyebrow">Packing list</p>',
    `<h1>${escapeHtml(title)}</h1>`,
    `<p class="subtitle">${escapeHtml(input.fromSite)} → ${escapeHtml(input.toSite)}</p>`,
    '</div>',
    `<div class="doc"><strong>${escapeHtml(input.docNo)}</strong>${input.scopeLabel ? `<span>${escapeHtml(input.scopeLabel)}</span>` : ''}</div>`,
    '</header>',
    '<section class="facts">',
    fact('Ships', input.ships),
    fact('Arrives', input.arrives),
    fact('Need by', input.needBy),
    fact('How it travels', input.logistics),
    fact('Events', events),
    '</section>',
    '<table>',
    '<thead><tr><th class="check" scope="col"><span class="box" aria-hidden="true"></span></th><th scope="col">Item</th><th scope="col">SKU</th><th class="qty" scope="col">Qty</th></tr></thead>',
    `<tbody>${body}</tbody>`,
    '</table>',
    '<div class="totals">',
    `<div><span>Lines</span><strong>${escapeHtml(String(input.lines.length))}</strong></div>`,
    `<div><span>Units</span><strong>${escapeHtml(String(units))}</strong></div>`,
    serialized ? `<div><span>Serialized</span><strong>${escapeHtml(String(serialized))}</strong></div>` : '',
    '</div>',
    '<section class="signatures">',
    '<div class="signature">Packed by · date</div>',
    '<div class="signature">Checked by</div>',
    '<div class="signature">Received by · date</div>',
    '</section>',
    `<p class="foot">Printed ${escapeHtml(input.printedAt)}</p>`,
    '</main>',
    input.autoPrint ? AUTO_PRINT_SCRIPT : '',
    '</body>',
    '</html>',
  ].join('');
}
